'use client';

import React from 'react';
import { Box } from '@mui/material';
import BaseModal from '@/app/base-ui/BaseModalUI';
import { LevelUpIcon } from '@/public/icon';
import { useTranslation } from '@/app/hooks/useTranslation';
import { useLanguage } from '@/app/contexts/language-context';

interface LevelUpData {
    level_current?: {
        id: number;
        name_ko: string;
        name_en: string;
        name_ja?: string;
        description?: string;
        icon_url?: string | null;
        point?: number;
    };
    level_new?: {
        id: number;
        name_ko: string;
        name_en: string;
        name_ja?: string;
        description?: string;
        icon_url?: string | null;
        point?: number;
    };
    benefit?: {
        id: number;
        level_id: number;
        name_ko: string;
        name_en: string;
        name_ja?: string;
        benefit_type: string;
        discount_type: string;
        value: string;
        description?: string;
    };
}

interface LevelUpModalProps {
    open: boolean;
    onClose: () => void;
    levelUpData: LevelUpData | null;
}

const LevelUpModal: React.FC<LevelUpModalProps> = ({ open, onClose, levelUpData }) => {
    const { t } = useTranslation();
    const { language } = useLanguage();

    if (!levelUpData || !levelUpData.level_new || !levelUpData.benefit) {
        return null;
    }

    // Get localized level name based on current language
    const getLocalizedName = (obj: any): string => {
        switch (language) {
            case 'en':
                return obj.name_en || obj.name_ko;
            case 'ja':
                return obj.name_ja || obj.name_ko;
            case 'ko':
            default:
                return obj.name_ko || obj.name_en;
        }
    };

    const levelName = getLocalizedName(levelUpData.level_new);
    const points = parseFloat(levelUpData.benefit.value || '0');

    const title = t('level_up_congratulations')
        .replace('{levelName}', levelName)
        .replace('{points}', points.toLocaleString());

    // Celebration icon
    const celebrationIcon = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <LevelUpIcon width={60} height={60} />
        </Box>
    );

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            title={title}
            description=""
            icon={celebrationIcon}
            showCancel={false}
            showAccept={true}
            onCancel={onClose}
            onAccept={onClose}
            acceptText={t('confirm', '확인')}
        />
    );
};

export default LevelUpModal;

